import { useFormik, Form, FormikProvider } from 'formik';
import { Stack, TextField, Grid, FormLabel, FormControl, Avatar, Button, Typography, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { UpdateProfileSchema } from '../../validations/UpdateProfileSchema';
import { useDispatch, useSelector } from 'react-redux';
import { saveUserProfile } from '../../services/user.service';
import { LOGIN } from '../../store/types';
import { useCallback, useState } from 'react';
import AvatarDialog from './AvatarDialog';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

export default function UpdateProfile() {
    const { auth } = useSelector(({ auth }) => ({ auth }));
    const dispatch = useDispatch();
    const [avatarModalOpen, setAvatarModalOpen] = useState(false)
    const formik = useFormik({
        initialValues: {
            id: auth?._id || '',
            fullName: auth?.fullName || '',
            jobTitle: auth?.jobTitle || '',
            avatar: auth?.avatar || '',
        },
        validationSchema: UpdateProfileSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true)
            await saveUserProfile(values).then(async (res) => {
                setSubmitting(false)
                if (res && res.data) {
                    await dispatch({ type: LOGIN, payload: res.data });
                }
            }).catch((err) => {
                setSubmitting(false)
            })
        }
    });

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps, values, setFieldValue } = formik;

    const handleChangeAvatar = useCallback(() => {
        setAvatarModalOpen(true);
    }, []);

    const handleCloseAvatar = useCallback(() => {
        setAvatarModalOpen(false);
    }, []);

    const handleClickAvatar = useCallback((name) => {
        setFieldValue("avatar", name)
        setAvatarModalOpen(false);
    }, [setFieldValue]);


    return (
        <Box>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar 
                                    sx={{ 
                                        width: 120, 
                                        height: 120, 
                                        border: '4px solid #F3F4F6',
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                    }} 
                                    alt={values.fullName} 
                                    src={`/assets/avatars/${values.avatar}.png`} 
                                />
                                <Button
                                    size="small"
                                    onClick={handleChangeAvatar}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        minWidth: 0,
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        backgroundColor: '#2259c9',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#1a44a0',
                                        }
                                    }}
                                >
                                    <CameraAltIcon sx={{ fontSize: 18 }} />
                                </Button>
                            </Box>
                            <Typography variant="subtitle2" sx={{ mt: 2, color: '#6B7280', fontWeight: 600 }}>
                                Profile Picture
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Stack spacing={3}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ mb: 1, fontWeight: 600, fontSize: '14px', color: '#374151' }}>Full Name</FormLabel>
                                    <TextField
                                        {...getFieldProps('fullName')}
                                        fullWidth
                                        autoComplete="off"
                                        placeholder="Enter your full name"
                                        error={Boolean(touched.fullName && errors.fullName)}
                                        helperText={(touched.fullName && errors.fullName)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: '10px',
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ mb: 1, fontWeight: 600, fontSize: '14px', color: '#374151' }}>Job Title</FormLabel>
                                    <TextField
                                        {...getFieldProps('jobTitle')}
                                        fullWidth
                                        autoComplete="off"
                                        placeholder="Enter your job title"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: '10px',
                                            }
                                        }}
                                    />
                                </FormControl>
                                
                                <Box sx={{ pt: 2 }}>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        loading={isSubmitting}
                                        sx={{
                                            py: 1.5,
                                            px: 4,
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#2259c9',
                                            '&:hover': {
                                                backgroundColor: '#1a44a0',
                                            }
                                        }}
                                    >
                                        Save Changes
                                    </LoadingButton>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </Form>
            </FormikProvider>
            {avatarModalOpen && (
                <AvatarDialog
                    handleClose={handleCloseAvatar}
                    handleClickAvatar={handleClickAvatar}
                />)
            }
        </Box>
    );
}
